import { useEffect, useState } from "react";
import { Pencil, Trash2, Eye, EyeOff, Star, MapPin, Calendar, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useDragReorder } from "@/lib/useDragReorder";
import Modal from "@/components/admin/Modal";
import { PageHeader, Field } from "./AdminFaq";

const EMPTY = { brand: "", title: "", description: "", image_url: "", location: "", year: "", category: "", order: 0, published: true, featured: false };

const CATEGORIES = [
    { id: "", label: "— Sem categoria —" },
    { id: "bebidas", label: "🍺 Bebidas & Bar" },
    { id: "cafe", label: "☕ Café & Padaria" },
    { id: "gelados", label: "🍦 Gelados & Doces" },
    { id: "comida", label: "🍣 Comida" },
    { id: "estrutura", label: "🛒 Estrutura base" },
];

export default function AdminEvents() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    async function load() {
        setLoading(true);
        try { const { data } = await api.get("/admin/events"); setItems(data || []); }
        catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Erro"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    function openCreate() { setEditing(null); setForm({ ...EMPTY, order: items.length + 1 }); setModalOpen(true); }
    function openEdit(it) { setEditing(it); setForm({ ...it, title: it.title || "", description: it.description || "", location: it.location || "", year: it.year || "", image_url: it.image_url || "", category: it.category || "" }); setModalOpen(true); }

    async function onSave(e) {
        e.preventDefault(); setSaving(true);
        try {
            const payload = {
                ...form,
                title: form.title?.trim() || null,
                description: form.description?.trim() || null,
                image_url: form.image_url?.trim() || null,
                location: form.location?.trim() || null,
                year: form.year?.trim() || null,
                category: form.category?.trim() || null,
            };
            if (editing) await api.put(`/admin/events/${editing.id}`, payload);
            else await api.post("/admin/events", payload);
            toast.success(editing ? "Atualizado" : "Criado"); setModalOpen(false); load();
        } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Erro"); }
        finally { setSaving(false); }
    }
    async function onDelete(it) {
        if (!window.confirm(`Apagar evento "${it.brand}"?`)) return;
        try { await api.delete(`/admin/events/${it.id}`); toast.success("Apagado"); load(); }
        catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Erro"); }
    }
    async function togglePublished(it) { try { await api.put(`/admin/events/${it.id}`, { published: !it.published }); load(); } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); } }
    async function toggleFeatured(it) { try { await api.put(`/admin/events/${it.id}`, { featured: !it.featured }); load(); } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); } }

    const { dragProps, draggingIndex, overIndex } = useDragReorder({ items, setItems, collection: "events" });

    return (
        <div data-testid="admin-events-page">
            <PageHeader title="Eventos & Marcas" subtitle="Casos de marca e ativações exibidas em /eventos — arraste para reordenar" onAdd={openCreate} addLabel="Novo caso" addTestId="event-add" count={items.length} />

            {loading ? (
                <div className="mt-4 p-8 text-center text-muted-foreground rounded-2xl border border-border bg-white">A carregar...</div>
            ) : (
                <div className="mt-4 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                    {items.map((it, idx) => (
                        <div key={it.id} data-testid={`event-card-${it.id}`} {...dragProps(idx)}
                            className={`relative rounded-2xl border bg-white overflow-hidden flex flex-col transition ${draggingIndex === idx ? "opacity-40" : ""} ${overIndex === idx && draggingIndex !== null && draggingIndex !== idx ? "border-secondary ring-2 ring-secondary/40" : "border-border"}`}>
                            <span data-testid={`event-drag-${it.id}`} title="Arrastar para reordenar"
                                className="absolute top-2 left-2 z-10 cursor-grab active:cursor-grabbing rounded-md bg-white/85 p-1 text-muted-foreground hover:text-primary backdrop-blur">
                                <GripVertical className="h-4 w-4" />
                            </span>
                            {it.image_url ? (
                                <img src={it.image_url} alt={it.brand} className="h-44 w-full object-cover" loading="lazy" />
                            ) : (
                                <div className="h-44 w-full bg-muted flex items-center justify-center text-muted-foreground text-sm">Sem imagem</div>
                            )}
                            <div className="p-5 flex-1 flex flex-col">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="font-heading text-lg font-bold text-primary">{it.brand}</div>
                                        {it.title && <div className="text-sm text-muted-foreground">{it.title}</div>}
                                    </div>
                                    <button onClick={() => toggleFeatured(it)} title={it.featured ? "Remover destaque" : "Destacar"}
                                        className={`rounded-md p-1.5 ${it.featured ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"}`}>
                                        <Star className={`h-4 w-4 ${it.featured ? "fill-amber-400" : ""}`} />
                                    </button>
                                </div>
                                <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                                    {it.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {it.location}</span>}
                                    {it.year && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {it.year}</span>}
                                    {it.category && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-primary/5 text-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                                            {(CATEGORIES.find(c => c.id === it.category)?.label) || it.category}
                                        </span>
                                    )}
                                </div>
                                {it.description && <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{it.description}</p>}
                                <div className="mt-auto pt-4 flex items-center gap-2 flex-wrap">
                                    <button onClick={() => togglePublished(it)} className={`text-xs rounded px-2 py-1 font-bold ${it.published ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                                        {it.published ? <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> Publicado</span> : <span className="inline-flex items-center gap-1"><EyeOff className="h-3 w-3" /> Oculto</span>}
                                    </button>
                                    <button onClick={() => openEdit(it)} data-testid={`event-edit-${it.id}`} className="text-xs rounded px-2 py-1 bg-muted hover:bg-zinc-200 inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Editar</button>
                                    <button onClick={() => onDelete(it)} data-testid={`event-delete-${it.id}`} className="text-xs rounded px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Apagar</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Editar evento" : "Novo evento"} size="lg">
                <form onSubmit={onSave} className="space-y-4" data-testid="event-form">
                    <div className="grid gap-4 sm:grid-cols-2">
                        <Field label="Marca">
                            <input required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
                        </Field>
                        <Field label="Título do caso (opcional)">
                            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
                        </Field>
                    </div>
                    <Field label="Descricão">
                        <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary resize-y" />
                    </Field>
                    <Field label="URL da imagem">
                        <input type="url" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
                    </Field>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <Field label="Localização"><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" /></Field>
                        <Field label="Ano"><input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" /></Field>
                        <Field label="Ordem"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" /></Field>
                    </div>
                    <Field label="Categoria (usada em /eventos e /galeria)">
                        <select
                            value={form.category}
                            onChange={(e) => setForm({ ...form, category: e.target.value })}
                            data-testid="event-category"
                            className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary"
                        >
                            {CATEGORIES.map((c) => (
                                <option key={c.id} value={c.id}>{c.label}</option>
                            ))}
                        </select>
                    </Field>
                    <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Destaque</label>
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Publicado</label>
                    </div>
                    <div className="flex justify-end gap-3 pt-3 border-t border-border">
                        <button type="button" onClick={() => setModalOpen(false)} className="px-5 py-2.5 text-sm font-semibold rounded-md border border-border hover:bg-muted">Cancelar</button>
                        <button type="submit" disabled={saving} data-testid="event-save" className="px-6 py-2.5 text-sm font-bold rounded-md bg-primary text-primary-foreground hover:-translate-y-0.5 transition disabled:opacity-60">
                            {saving ? "A guardar..." : (editing ? "Atualizar" : "Criar")}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
